@let hasShiftUpdatePermission =
  can(RoleAction.Update, RolePermissionSubject.Shift);
@let hasStayReadPermission = can(RoleAction.Read, RolePermissionSubject.Stay);

@if (
  {
    unreleasedShifts: unreleasedShifts$ | async,
    userOvertimeAverage: userOvertimeAverage$ | async,
    hasSuccessfullyFetchedUsers: hasSuccessfullyFetchedUsers$ | async,
    isLoadingUnreleasedShifts: isLoadingUnreleasedShifts$ | async,
    unassignedShiftsWithoutActivities:
      unassignedShiftsWithoutActivitiesCount$ | async,
    renderOvertimeAccountKpi: showOvertimeAccountKpi$ | async,
    renderProductivityKpi: showProductivityKpi$ | async,
    overallUserProductivity: overallUserProductivity$ | async,
    isLoadingUsersProductivity: isLoadingUsersProductivity$ | async,
    hasSuccessfullyFetchedUsersProductivity:
      hasSuccessfullyFetchedUsersProductivity$ | async,
    visibleDateRange: visibleDateRange$ | async,
    showDeclinedShifts: showDeclinedShifts$ | async,
    declinedShifts: declinedShifts$ | async,
    canRenderStay: timelineStaysSuggestionService.canRenderStays$ | async,
  };
  as kpiData
) {
  <div class="kpi-card">
    <div class="kpi-time-range">
      @if (kpiData.visibleDateRange?.start && kpiData.visibleDateRange?.end) {
        @if (isKPICardOpen) {
          {{ kpiData.visibleDateRange?.start | localizedDateTimeFormat }} —
          {{ kpiData.visibleDateRange?.end | localizedDateTimeFormat }}
        } @else {
          {{ kpiData.visibleDateRange?.start | localizedDayMonthFormat }} —
          {{ kpiData.visibleDateRange?.end | localizedDayMonthFormat }}
        }
      }
    </div>
    <div class="d-flex align-items-center">
      <!-- Stay -->
      @if (kpiData.canRenderStay && isKPICardOpen) {
        <div
          class="kpi-item"
          animate.enter="kpi-slide-enter"
          animate.leave="kpi-slide-leave"
        >
          @if (hasStayReadPermission) {
            <wilson-stays-kpi></wilson-stays-kpi>
          } @else {
            <div
              class="kpi-counter-section disabled-card"
              data-test-id="wilson-timeline-header-kpi-section:stays-kpi"
              nz-tooltip
              [nzTooltipTitle]="'general.forbidden_action' | translate"
            >
              <p class="kpi-header-text p-0 m-0 stay-title text-truncate">
                {{ 'page.shift_timeline.kpi.stays' | translate }}
              </p>
              <span>{{ 'general.n/a' | translate }}</span>
            </div>
          }
        </div>
      }

      <!-- Unoccupied Shifts -->
      @if (isKPICardOpen) {
        <div
          class="kpi-item"
          animate.enter="kpi-slide-enter"
          animate.leave="kpi-slide-leave"
        >
          <div class="kpi-counter-section">
            <p class="kpi-header-text p-0 m-0">
              {{ 'page.shift_timeline.unassigned_shifts_tab' | translate }}
            </p>
            <p class="kpi-counter-text p-0 m-0">
              {{ kpiData.unassignedShiftsWithoutActivities || 0 }}
            </p>
          </div>
        </div>
      }

      <!-- Declined Shifts -->
      @if (kpiData.showDeclinedShifts && isKPICardOpen) {
        <div
          class="kpi-item"
          animate.enter="kpi-slide-enter"
          animate.leave="kpi-slide-leave"
        >
          <div
            (click)="activateDeclineFilter()"
            class="kpi-counter-section"
            [class.cursor-pointer]="
              kpiData.hasSuccessfullyFetchedUsers &&
              !kpiData.isLoadingUnreleasedShifts
            "
          >
            <p
              class="kpi-header-text p-0 m-0"
              [ngClass]="
                !kpiData.hasSuccessfullyFetchedUsers ||
                kpiData.isLoadingUnreleasedShifts
                  ? 'kpi-header-loading-text'
                  : 'kpi-header-text'
              "
            >
              {{ 'page.shift_timeline.declined_shifts_tab' | translate }}
            </p>
            <p
              [ngClass]="
                !kpiData.hasSuccessfullyFetchedUsers ||
                kpiData.isLoadingUnreleasedShifts
                  ? 'kpi-counter-loading-text '
                  : 'kpi-counter-text '
              "
              class="p-0 m-0"
            >
              {{ kpiData.declinedShifts?.length || '0' }}
            </p>
          </div>
        </div>
      }

      <!-- Overtime -->
      @if (kpiData.renderOvertimeAccountKpi && isKPICardOpen) {
        <div
          class="kpi-item"
          animate.enter="kpi-slide-enter"
          animate.leave="kpi-slide-leave"
        >
          <div class="kpi-counter-section">
            <div class="d-flex">
              <div>
                <p class="kpi-averages-header-text p-0 m-0">
                  {{
                    'page.shift_timeline.unassigned_shifts_tab.average_over_time'
                      | translate
                  }}
                </p>
                <p class="kpi-counter-text p-0 m-0">
                  {{ kpiData.userOvertimeAverage | minutesToAverageHours }}
                </p>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Productivity -->
      @if (kpiData.renderProductivityKpi && isKPICardOpen) {
        <div
          class="kpi-item"
          animate.enter="kpi-slide-enter"
          animate.leave="kpi-slide-leave"
        >
          <div class="kpi-counter-section">
            <div class="d-flex">
              <div>
                <p class="kpi-averages-header-text p-0 m-0">
                  {{
                    'page.shift_timeline.unassigned_shifts_tab.average_productivity'
                      | translate
                  }}
                </p>
                <p
                  [ngClass]="
                    !kpiData.hasSuccessfullyFetchedUsersProductivity ||
                    kpiData.isLoadingUsersProductivity
                      ? 'kpi-counter-loading-text '
                      : 'kpi-counter-text '
                  "
                  class="p-0 m-0"
                >
                  {{ kpiData.overallUserProductivity + '%' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Unpublished Shifts  -->
      <div class="kpi-item kpi-item-last">
        <div
          class="kpi-counter-section"
          [class.cursor-pointer]="
            hasShiftUpdatePermission &&
            kpiData.hasSuccessfullyFetchedUsers &&
            !kpiData.isLoadingUnreleasedShifts
          "
          [class.not-allowed]="!hasShiftUpdatePermission"
          nz-tooltip
          [nzTooltipTitle]="
            !hasShiftUpdatePermission
              ? ('general.forbidden_action' | translate)
              : null
          "
          nz-popconfirm
          [nzPopconfirmTrigger]="
            hasShiftUpdatePermission &&
            kpiData.unreleasedShifts?.length &&
            !kpiData.isLoadingUnreleasedShifts
              ? 'click'
              : null
          "
          [nzPopconfirmTitle]="
            'page.shift_timeline.unreleased_shifts.confirm_message' | translate
          "
          (nzOnConfirm)="publishUnreleasedShifts(kpiData.unreleasedShifts)"
          data-test-id="wilson-timeline-header-kpi-section:unreleased-shifts"
        >
          <p
            [ngClass]="
              !kpiData.hasSuccessfullyFetchedUsers ||
              kpiData.isLoadingUnreleasedShifts
                ? 'kpi-header-loading-text'
                : 'kpi-header-text'
            "
            class="p-0 m-0"
          >
            {{ 'page.shift_timeline.unreleased_shifts' | translate }}
          </p>
          <p
            [ngClass]="
              !kpiData.hasSuccessfullyFetchedUsers ||
              kpiData.isLoadingUnreleasedShifts
                ? 'kpi-counter-loading-text '
                : 'kpi-counter-text '
            "
            class="p-0 m-0"
          >
            {{ kpiData.unreleasedShifts?.length || '0' }}
          </p>
        </div>
      </div>
    </div>
  </div>
}
